import { computed, type ComputedRef, type Ref } from 'vue';
import type { BookCanvas } from '@/modules/editor/canvas/BookCanvas.class';
import type { BookCore } from '@/modules/flipcore/BookCore';
import { useProjectStore } from '@/store/project';
import { getVisibleBookRect } from '@/views/book/pageTurnHitTest';
import type { DisplayMode } from '@/types/book';
import { resolveReaderBookLayout, resolveReaderBookTransform } from './readerBookLayout';

interface CanvasSize {
  width: number;
  height: number;
}

interface UseReaderLayoutOptions {
  canvasSize: ComputedRef<CanvasSize>;
  isPC: Ref<boolean>;
  getCanvas: () => BookCanvas | undefined;
  getCurrentBook: () => BookCore | null;
  activeDisplayMode: Ref<DisplayMode>;
  totalPages: ComputedRef<number> | Ref<number>;
}

/**
 * 布局域（纯计算）：按 canvas 尺寸与展示模式推导书本实际尺寸与占位、
 * 点击翻页命中矩形与控制条锚定基线。
 */
export const useReaderLayout = (options: UseReaderLayoutOptions) => {
  const {
    canvasSize,
    isPC,
    getCanvas,
    getCurrentBook,
    activeDisplayMode,
    totalPages
  } = options;

  const projectStore = useProjectStore();

  /** 按当前 canvas 尺寸与展示模式计算书本实际像素尺寸。 */
  const getBookLayout = (displayMode: DisplayMode) => {
    const previewBook = projectStore.book;
    const page = previewBook.pages?.[0];
    if (!page) return null;

    const { width: canvasWidth, height: canvasHeight } = canvasSize.value;
    return resolveReaderBookLayout({
      canvasWidth,
      canvasHeight,
      isPC: isPC.value,
      pageWidth: previewBook.width ?? page.width ?? 0,
      pageHeight: previewBook.height ?? page.height ?? 0,
      displayMode,
      displayRect: previewBook.displayRect
    });
  };

  /** 普通 resize 基于首次渲染尺寸计算 root 的缩放与位置，不重建页面。 */
  const getBookTransform = (book: BookCore, displayMode: DisplayMode) => {
    const { width: canvasWidth, height: canvasHeight } = canvasSize.value;
    return resolveReaderBookTransform({
      canvasWidth,
      canvasHeight,
      isPC: isPC.value,
      pageWidth: book.pageWidth,
      pageHeight: book.pageHeight,
      displayMode,
      displayRect: projectStore.book.displayRect
    });
  };

  const getRenderedBookRect = () => {
    const book = getCurrentBook();
    if (!book) return null;
    const transform = getBookTransform(book, activeDisplayMode.value);
    if (!transform) return null;

    const { pageWidth, pageHeight, scale, displayRect, canvasWidth, canvasHeight } = transform;
    return {
      rect: getVisibleBookRect({
        canvasWidth,
        canvasHeight,
        offsetX: displayRect.x,
        offsetY: displayRect.y,
        pageWidth,
        pageHeight,
        scale,
        displayMode: activeDisplayMode.value,
        currentPageIndex: book.currentPageIndex,
        pageCount: totalPages.value
      }),
      pageWidth: pageWidth * scale,
      canvasWidth,
      canvasHeight
    };
  };

  const applyBookDisplayRect = (book: BookCore, displayMode: DisplayMode) => {
    const root = book.root;
    const transform = getBookTransform(book, displayMode);
    if (!root || !transform) return;

    const { scale, bookWidth, bookHeight, displayRect } = transform;
    const x = displayRect.x - (bookWidth * scale) / 2;
    const y = displayRect.y - (bookHeight * scale) / 2;

    root.scale.set(scale);
    root.position.set(x, y);
  };

  const syncBookDisplayRect = () => {
    const book = getCurrentBook();
    if (!book) return;

    // 缩放模式下 root 变换由 BookZoomController 独占管理，
    // 此处跳过布局同步，避免离开舞台隐藏控制条时把书本强行重置回 100%。
    if (getCanvas()?.isZooming()) return;

    applyBookDisplayRect(book, activeDisplayMode.value);
  };

  /**
   * 书本底边相对 canvas 元素顶部的距离（CSS px）。
   * 舞台原点居中（canvasHeight / 2），书本相对中心偏移 displayRect.y，再加半个书高。
   */
  const bookContentBottom = computed(() => {
    const book = getCurrentBook();
    if (!book) return null;
    const transform = getBookTransform(book, activeDisplayMode.value);
    if (!transform) return null;

    const { scale, bookHeight, displayRect, canvasHeight } = transform;
    return canvasHeight / 2 + displayRect.y + (bookHeight * scale) / 2;
  });

  return {
    getBookLayout,
    applyBookDisplayRect,
    syncBookDisplayRect,
    getRenderedBookRect,
    bookContentBottom
  };
};
